import * as React from 'react'
import {
  IconButton,
  Menu,
  Tooltip,
  Avatar,
  Typography
} from '@mui/material';
import PropTypes, { InferProps } from 'prop-types';
import {v4 as uuidv4} from "uuid";
import { useTranslation } from 'react-i18next';
import stringAvatar from './stringAvatar';
import ButtonLinkMenu from '../ButtonLinkMenu';
import StateContext from 'src/context/UserProvider';
import { useAuthMe } from 'src/context/AuthMeContext';

/***
 * @function UserNavBar
 * @description Image user an username
 * @param {?Element} settings: Optional, icon to display.
 * {
 *   {string} name: Text to display.
 *   {string} url: Section or URL to direct to. The URL starts with https://
 *   {?element} icon: Optional, icon to display.
 *   {?boolean} divider: Optional, add a divider above the button.
 * }
 *
 * @example
 *   <UserNavBar
 *     userName="Maria Carolina Gomez"
 *     userImg="https://images.unsplash.com/photo-1438761681033-6461ffad8d80?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxzZWFyY2h8Mnx8cGVyc29ufGVufDB8fDB8fA%3D%3D&w=1000&q=80"
 *     settings= {
 *       [
 *         {
 *           name:"Settings",
 *           url:"/Setting",
 *           icon:<SettingsIcon  sx={{ fontSize:16 }} />
 *         },
 *         {
 *           name:"Help",
 *           url:"https://www.google.com",
 *           divider:true
 *         }
 *       ]
 *      }
 *   />
 *
 */
function UserNavBar({ settings }: InferProps<typeof UserNavBar.propTypes>) {
  const { user } = StateContext();
  const { authMe } = useAuthMe();
  const [anchorElUser, setAnchorElUser] = React.useState<null | HTMLElement>(null);

  const { t } = useTranslation();

  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget)
  }

  const handleCloseUserMenu = () => {
    setAnchorElUser(null)
  }

  const isImage = (): boolean => {
    return user !== null && user.photo !== "" && user.photo.toString().trim().length > 0
  }

  const noPointer = { cursor: 'default' }

  const avatarStyle = {
    width: { xs: 22, sm: 24, md: 26 },
    height: { xs: 22, sm: 24, md: 26 }
  }

  return (
    <IconButton size="large" edge="start" color="inherit" aria-label="menu" sx={{ mr: 2  }} style={noPointer} >
      {
        (settings != null)
          ? (
            <Tooltip title={t("layout.openSettings")} >
              <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }} >
                {
                  isImage()
                    ? (user !== null) && <Avatar sx={avatarStyle} alt={user.getFullName()} src={`${user.photo}`} />
                    : (user !== null) && <Avatar sx={avatarStyle} {...stringAvatar(user.getFullName())} />
                }
              </IconButton>
            </Tooltip>
          )
          : (
            <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }} style={noPointer} >
              {
                isImage()
                  ? user && <Avatar sx={avatarStyle} alt={user.getFullName()} src={`${user.photo}`} style={noPointer} />
                  : user && <Avatar sx={avatarStyle} {...stringAvatar(user.getFullName())} style={noPointer} />
              }
            </IconButton>
          )
      }
      <p
        style={{
          margin: 0,
          marginLeft: '0.5rem',
          padding: 0,
          transform: 'translateY(-4px)',
        }}
      >
        {user && (
          <Typography
            component="span"
            sx={{ fontSize: { xs: "0.9rem", sm: "1rem", md:"1.2rem" } }}
          >
            {user.getFullName()}
          </Typography>
        )}
        {authMe?.role?.name && (
          <Typography
            component="span"
            display="block"
            sx={{ 
              fontSize: 
              { xs: "0.65rem", sm: "0.7rem", md: "0.75rem" },
              opacity: 0.75, 
              lineHeight: 1.2 
            }}
          >
            {authMe.role.name}
          </Typography>
        )}
      </p>
      {
        !(settings == null) && <Menu
          sx={{ mt: '45px' }}
          id="menu-appbar"
          anchorEl={anchorElUser}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right'
          }}
          keepMounted
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right'
          }}
          open={Boolean(anchorElUser)}
          onClose={handleCloseUserMenu}
        >
          {
            settings.map((item) => (item != null) &&
              <>
                <ButtonLinkMenu
                  key={uuidv4()}
                  strName={item.name}
                  strUrl={item.url}
                  elementIcon={item.icon}
                  divider={item.divider}
                  fncClose={handleCloseUserMenu} />
              </>
            )
          }
        </Menu>
      }
    </IconButton>
  )
}

UserNavBar.propTypes = {
  settings: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired,
      icon: PropTypes.element,
      divider: PropTypes.bool
    })
  )
}

export default UserNavBar
